import GlobalCollapsibleButton from './GlobalCollapsibleButton';
import {
  formatEuroOptional,
  formatRunway,
} from '../utils/formatters';
import type { FounderUpdateIntelligence, UpdateSentiment } from '../types';
import styles from './FounderUpdateIntelligenceSections.module.css';

type FounderUpdateIntelligenceSectionsProps = {
  intelligence: FounderUpdateIntelligence;
};

const HEALTH_PILL_CLASS: Record<FounderUpdateIntelligence['healthSignal'], string> = {
  improving: styles.healthImproving,
  stable: styles.healthStable,
  deteriorating: styles.healthDeteriorating,
  unclear: styles.healthUnclear,
};

const CONFIDENCE_PILL_CLASS: Record<
  FounderUpdateIntelligence['extractedMetrics']['confidence'],
  string
> = {
  high: styles.confidenceHigh,
  medium: styles.confidenceMedium,
  low: styles.confidenceLow,
};

const SEVERITY_PILL_CLASS = {
  high: styles.severityHigh,
  medium: styles.severityMedium,
  low: styles.severityLow,
} as const;

const URGENCY_PILL_CLASS = {
  high: styles.urgencyHigh,
  medium: styles.urgencyMedium,
  low: styles.urgencyLow,
} as const;

function sentimentClassName(sentiment: UpdateSentiment): string {
  return `sentiment sentiment-${sentiment}`;
}

/** Compact sentiment + health-signal pills, reusable outside the full analysis sections. */
export function FounderUpdateSignalPills({
  sentiment,
  healthSignal,
}: {
  sentiment: UpdateSentiment;
  healthSignal: FounderUpdateIntelligence['healthSignal'];
}) {
  return (
    <>
      <span className={sentimentClassName(sentiment)}>{sentiment}</span>
      <span className={`${styles.healthPill} ${HEALTH_PILL_CLASS[healthSignal]}`}>
        {healthSignal}
      </span>
    </>
  );
}

function NoneListed() {
  return <p className={`${styles.emptyState} muted`}>None listed.</p>;
}

function BulletList({ items }: { items: string[] }) {
  const entries = items.map((item) => item.trim()).filter(Boolean);

  if (entries.length === 0) {
    return <NoneListed />;
  }

  return (
    <ul className={styles.bulletList}>
      {entries.map((item, index) => (
        <li key={`${item}-${index}`} className={styles.bulletItem}>
          {item}
        </li>
      ))}
    </ul>
  );
}

export function FounderUpdateIntelligenceSections({
  intelligence,
}: FounderUpdateIntelligenceSectionsProps) {
  const {
    summary,
    sentiment,
    healthSignal,
    extractedMetrics,
    whatChanged,
    milestones,
    risks,
    asks,
    followUpQuestions,
  } = intelligence;

  const metricRows = [
    { label: 'Revenue', value: formatEuroOptional(extractedMetrics.revenue) },
    { label: 'Burn', value: formatEuroOptional(extractedMetrics.burn) },
    { label: 'Cash', value: formatEuroOptional(extractedMetrics.remainingCash) },
    {
      label: 'Runway',
      value: formatRunway(extractedMetrics.runwayMonths),
    },
    {
      label: 'Headcount',
      value:
        extractedMetrics.headcount !== undefined
          ? extractedMetrics.headcount.toLocaleString()
          : '—',
    },
  ];

  return (
    <div className={styles.root} aria-label="Founder update analysis">
      <div className={styles.signalRow}>
        <FounderUpdateSignalPills sentiment={sentiment} healthSignal={healthSignal} />
      </div>

      <GlobalCollapsibleButton title="Summary" size="comfortable" isOpen>
        <p className={styles.prose}>{summary}</p>
      </GlobalCollapsibleButton>

      <GlobalCollapsibleButton title="Extracted metrics" size="comfortable">
        <div className={styles.metricsGrid}>
          {metricRows.map(({ label, value }) => (
            <div key={label} className={styles.metricCard}>
              <p className={styles.metricLabel}>{label}</p>
              <p className={styles.metricValue}>{value}</p>
            </div>
          ))}
        </div>
        <div className={styles.metricsMeta}>
          <span
            className={`${styles.confidencePill} ${CONFIDENCE_PILL_CLASS[extractedMetrics.confidence]}`}
          >
            {extractedMetrics.confidence} confidence
          </span>
        </div>
        {extractedMetrics.notes.trim() ? (
          <p className={styles.prose}>{extractedMetrics.notes}</p>
        ) : null}
      </GlobalCollapsibleButton>

      <GlobalCollapsibleButton title="What changed" size="comfortable">
        <BulletList items={whatChanged} />
      </GlobalCollapsibleButton>

      <GlobalCollapsibleButton title="Milestones" size="comfortable">
        <BulletList items={milestones} />
      </GlobalCollapsibleButton>

      <GlobalCollapsibleButton title="Risks" size="comfortable">
        {risks.length === 0 ? (
          <NoneListed />
        ) : (
          <ul className={styles.risksList}>
            {risks.map((risk, index) => (
              <li key={`${risk.description}-${index}`} className={styles.riskItem}>
                <div className={styles.riskHeader}>
                  <p className={styles.riskDescription}>{risk.description}</p>
                  <span
                    className={`${styles.severityPill} ${SEVERITY_PILL_CLASS[risk.severity]}`}
                  >
                    {risk.severity}
                  </span>
                </div>
                <p className={styles.riskRationale}>{risk.rationale}</p>
              </li>
            ))}
          </ul>
        )}
      </GlobalCollapsibleButton>

      <GlobalCollapsibleButton title="Asks" size="comfortable">
        {asks.length === 0 ? (
          <NoneListed />
        ) : (
          <ul className={styles.asksList}>
            {asks.map((ask, index) => (
              <li key={`${ask.description}-${index}`} className={styles.askItem}>
                <div className={styles.askHeader}>
                  <p className={styles.askDescription}>{ask.description}</p>
                  <div className={styles.signalRow}>
                    <span className={styles.askTypePill}>{ask.type}</span>
                    <span className={`${styles.urgencyPill} ${URGENCY_PILL_CLASS[ask.urgency]}`}>
                      {ask.urgency} urgency
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </GlobalCollapsibleButton>

      <GlobalCollapsibleButton title="Investor follow-up questions" size="comfortable">
        {followUpQuestions.length === 0 ? (
          <NoneListed />
        ) : (
          <ol className={styles.questionsList}>
            {followUpQuestions.map((question, index) => (
              <li key={`${question}-${index}`} className={styles.questionItem}>
                {question}
              </li>
            ))}
          </ol>
        )}
      </GlobalCollapsibleButton>
    </div>
  );
}
